import type { LogFilter } from '../../api/stats';

/**
 * Muting an error group = an exclude filter in each organization's log filters (the collector's ingest drops a log
 * record whose message matches it). The regex comes from the group's title, the collector's normalized first line
 * (`ErrorFingerprint.Normalize`): literal text escaped, its placeholders turned back into what they replaced, runs of
 * whitespace as `\s+`, anchored at the message's first non-empty line. It is run by .NET at ingest; the constructs
 * used here mean the same in JavaScript, so the console can test it against the group's sample.
 */
const PLACEHOLDERS: [string, string][] = [
  ["'<str>'", `(?:'[^'\\r\\n]*'|"[^"\\r\\n]*")`],
  ['<guid>', '[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}'],
  ['<ts>', '\\d{4}-\\d{2}-\\d{2}[T ][\\d:.,]+(?:Z|[+-]\\d{2}:?\\d{2})?'],
  ['<date>', '\\d{1,4}[-/.]\\d{1,2}[-/.]\\d{1,4}'],
  ['<time>', '\\d{1,2}:\\d{2}(?::\\d{2}(?:[.,]\\d+)?)?'],
  ['<hex>', '\\w+'],
  ['<n>', '\\w+'],
];

const escape = (s: string) => s.replace(/[\\^$.|?*+()[\]{}]/g, '\\$&');

/** The regex that matches the messages of the group with this title. */
export function muteRegex(title: string): string {
  // a title cut at 300 characters may end inside a placeholder
  const text = title.trim().replace(/'?<[a-z]*$/, '');
  let out = '';
  let i = 0;
  while (i < text.length) {
    const hit = PLACEHOLDERS.find(([p]) => text.startsWith(p, i));
    if (hit) {
      out += hit[1];
      i += hit[0].length;
    } else if (/\s/.test(text[i])) {
      while (i < text.length && /\s/.test(text[i])) i++;
      out += '\\s+';
    } else {
      out += escape(text[i]);
      i++;
    }
  }
  return `^\\s*${out}`;
}

/** Whether `regex` compiles (JavaScript's engine; the collector checks it again with .NET's). */
export function compiles(regex: string): boolean {
  try {
    new RegExp(regex);
    return true;
  } catch {
    return false;
  }
}

/** Whether `regex` matches `message` (false when it does not compile here). */
export function matches(regex: string, message: string): boolean {
  try {
    return new RegExp(regex).test(message);
  } catch {
    return false;
  }
}

/**
 * The organization's filters with the mute in front (first match wins), or null when an exclude filter with this
 * regex is already there. Without filters the collector stores every log, while with filters it stores only what an
 * include filter matches: then a catch-all include (severity 1, as without filters) keeps everything else stored.
 * The mute's line sorts before every other line, so a list ordered by line keeps it first too.
 */
export function withMute(filters: LogFilter[], regex: string): LogFilter[] | null {
  if (filters.some((f) => !f.include && f.regex === regex)) return null;
  const first = filters.length ? Math.min(...filters.map((f) => f.line)) - 1 : 1;
  const mute: LogFilter = { line: first, regex, include: false, severity: null };
  return filters.length ? [mute, ...filters] : [mute, { line: 2, regex: '.*', include: true, severity: 1 }];
}
